import { mapActions, mapGetters } from 'vuex';
import { policyholderTypes } from '@/entities/insurance/config/policyholder-type';

/**
 *
 * @param storeModule
 */
export function PetsPolicyholderState(storeModule) {
  return {
    data() {
      return {
        policyholder: {
          id: null,
          type: 'natural',
          fullName: null,
          taxNumber: null,
          withoutTaxNumber: false,
          address: {
            region: null,
            city: null,
            street: { nameUa: null, nameEn: null },
            buildingNumber: null,
            apartmentNumber: null,
          },
          email: null,
          phone: null,
          dateOfBirth: null,
          documentsList: [],
        },
      };
    },
    mounted() {
      this.policyholder = {
        ...this.policyholder,
        id: this.getPolicyholderId,
        type: this.getPolicyholderType,
        fullName: this.getPolicyholderFullName,
        taxNumber: this.getPolicyholderTaxNumber,
        withoutTaxNumber: this.isPolicyholderWithoutTaxNumber,
        address: {
          region: null,
          city: null,
          street: { nameUa: null, nameEn: null },
          buildingNumber: null,
          apartmentNumber: null,
          ...this.getPolicyholderAddress,
        },
        email: this.getPolicyholderEmail,
        phone: this.getPolicyholderPhone,
        dateOfBirth: this.getPolicyholderDateOfBirth,
      };
    },
    computed: {
      ...mapGetters(storeModule, [
        'getPolicyholderId', 'getPolicyholderType', 'getPolicyholderFullName', 'getPolicyholderTaxNumber',
        'getPolicyholderAddress', 'getPolicyholderEmail', 'getPolicyholderPhone', 'getPolicyholderDateOfBirth',
        'getPolicyholderDocumentsList', 'isPolicyholderWithoutTaxNumber',
      ]),
      getLastNameUa() {
        const parts = this.policyholder.fullName?.partsUa ||
          this.parseFullNameToPartsUa(this.policyholder.fullName?.fullNameUa);
        return parts[0] || null;
      },
      getFirstNameUa() {
        const parts = this.policyholder.fullName?.partsUa ||
          this.parseFullNameToPartsUa(this.policyholder.fullName?.fullNameUa);
        return parts[1] || null;
      },
      getMiddleNameUa() {
        const parts = this.policyholder.fullName?.partsUa ||
          this.parseFullNameToPartsUa(this.policyholder.fullName?.fullNameUa);
        return parts[2] || null;
      },
      getLastNameEn() {
        const parts = this.policyholder.fullName?.partsEn ||
          this.parseFullNameToPartsEn(this.policyholder.fullName?.fullNameEn);
        return parts[0] || null;
      },
      getFirstNameEn() {
        const parts = this.policyholder.fullName?.partsEn ||
          this.parseFullNameToPartsEn(this.policyholder.fullName?.fullNameEn);
        return parts[1] || null;
      },
    },
    methods: {
      ...mapActions(storeModule, [
        'setPolicyholderId', 'setPolicyholderType', 'setPolicyholderFullName', 'setPolicyholderTaxNumber',
        'setPolicyholderAddress', 'setPolicyholderEmail', 'setPolicyholderPhone', 'setPolicyholderDateOfBirth',
        'setPolicyholderDocumentsList', 'setPolicyholderWithoutTaxNumber',
      ]),
      parseFullNameToPartsUa(fullName) {
        if (!fullName || typeof fullName !== 'string') return ['', '', ''];

        const nameParts = fullName.split(' ').filter(Boolean);

        if (nameParts.length >= 3) {
          return [nameParts[0] || '', nameParts[1] || '', nameParts.slice(2).join(' ') || ''];
        } else if (nameParts.length === 2) {
          return [nameParts[0] || '', nameParts[1] || '', ''];
        } else if (nameParts.length === 1) {
          return [nameParts[0] || '', '', ''];
        }

        return ['', '', ''];
      },
      parseFullNameToPartsEn(fullName) {
        if (!fullName || typeof fullName !== 'string') return ['', ''];

        const nameParts = fullName.split(' ').filter(Boolean);

        return [nameParts[0] || '', nameParts[1] || ''];
      },
      calculateFullNameFromPartsUa(parts) {
        if (!parts || !Array.isArray(parts)) return null;

        const [lastName = '', firstName = '', middleName = ''] = parts;
        return [lastName, firstName, middleName].filter(Boolean).join(' ') || null;
      },
      calculateFullNameFromPartsEn(parts) {
        if (!parts || !Array.isArray(parts)) return null;

        const [lastName = '', firstName = ''] = parts;
        return [lastName, firstName].filter(Boolean).join(' ') || null;
      },
      setLastNameUa(lastName) {
        const currentParts = this.policyholder.fullName?.partsUa ||
          this.parseFullNameToPartsUa(this.policyholder.fullName?.fullNameUa);

        const newParts = [lastName || '', currentParts[1] || '', currentParts[2] || ''];

        this.policyholder.fullName = {
          ...this.policyholder.fullName,
          partsUa: newParts,
          fullNameUa: this.calculateFullNameFromPartsUa(newParts),
        };
      },
      setFirstNameUa(firstName) {
        const currentParts = this.policyholder.fullName?.partsUa ||
          this.parseFullNameToPartsUa(this.policyholder.fullName?.fullNameUa);

        const newParts = [currentParts[0] || '', firstName || '', currentParts[2] || ''];

        this.policyholder.fullName = {
          ...this.policyholder.fullName,
          partsUa: newParts,
          fullNameUa: this.calculateFullNameFromPartsUa(newParts),
        };
      },
      setMiddleNameUa(middleName) {
        const currentParts = this.policyholder.fullName?.partsUa ||
          this.parseFullNameToPartsUa(this.policyholder.fullName?.fullNameUa);

        const newParts = [currentParts[0] || '', currentParts[1] || '', middleName || ''];

        this.policyholder.fullName = {
          ...this.policyholder.fullName,
          partsUa: newParts,
          fullNameUa: this.calculateFullNameFromPartsUa(newParts),
        };
      },
      setLastNameEn(lastName) {
        const currentParts = this.policyholder.fullName?.partsEn ||
          this.parseFullNameToPartsEn(this.policyholder.fullName?.fullNameEn);

        const newParts = [lastName || '', currentParts[1] || ''];

        this.policyholder.fullName = {
          ...this.policyholder.fullName,
          partsEn: newParts,
          fullNameEn: this.calculateFullNameFromPartsEn(newParts),
        };
      },
      setFirstNameEn(firstName) {
        const currentParts = this.policyholder.fullName?.partsEn ||
          this.parseFullNameToPartsEn(this.policyholder.fullName?.fullNameEn);

        const newParts = [currentParts[0] || '', firstName || ''];

        this.policyholder.fullName = {
          ...this.policyholder.fullName,
          partsEn: newParts,
          fullNameEn: this.calculateFullNameFromPartsEn(newParts),
        };
      },
      policyholderClear() {
        this.policyholder = {
          ...this.policyholder,
          id: null,
          type: null,
          fullName: null,
          taxNumber: null,
          withoutTaxNumber: false,
          address: {
            region: null,
            city: null,
            street: { nameUa: null, nameEn: null },
            buildingNumber: null,
            apartmentNumber: null,
          },
          email: null,
          phone: null,
          dateOfBirth: null,
          documentsList: [],
        };
      },
    },
    watch: {
      'policyholder.id'(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.setPolicyholderId(newValue);
        }
      },
      'policyholder.type'(newValue, oldValue) {
        if (newValue !== oldValue) {
          const policyholderType = policyholderTypes?.find(type => type.key === newValue);
          if (policyholderType) {
            this.policyholder.type = policyholderType.key;
            this.setPolicyholderType(policyholderType.key);
          }
        }
      },
      'policyholder.fullName'(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.setPolicyholderFullName(newValue);
        }
      },
      'policyholder.taxNumber'(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.setPolicyholderTaxNumber(newValue);
        }
      },
      'policyholder.withoutTaxNumber'(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.setPolicyholderWithoutTaxNumber(newValue);
        }
      },
      'policyholder.address'(newValue) {
        this.setPolicyholderAddress(newValue);
      },
      'policyholder.email'(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.setPolicyholderEmail(newValue);
        }
      },
      'policyholder.phone'(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.setPolicyholderPhone(newValue);
        }
      },
      'policyholder.dateOfBirth'(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.setPolicyholderDateOfBirth(newValue);
        }
      },
      'policyholder.documentsList'(newValue) {
        this.setPolicyholderDocumentsList(newValue);
      },
      isPolicyholderWithoutTaxNumber(newValue) {
        this.policyholder.withoutIdentificationCode = newValue;
      },
    },
  };
}